import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PunchLogs = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [employees, setEmployees] = useState([]);
  const [punches, setPunches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true });
      setEmployees(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany) { fetchEmployees(); }
  }, [selectedCompany, fetchEmployees]);
  const fetchPunches = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/attendance/punches?punch_date=${selectedDate}`, { withCredentials: true });
      setPunches(data || []);
    } catch (e) {
      console.error(e);
      setPunches([]);
    } finally { setLoading(false); }
  }, [selectedDate]);

  useEffect(() => { fetchPunches(); }, [selectedDate, fetchPunches]);




  const getEmployee = (empId) => employees.find(e => e.id === empId);

  const getEmployeeName = (empId) => {
    const e = getEmployee(empId);
    if (!e) return empId;
    return `${e.personal_details?.first_name || ''} ${e.personal_details?.last_name || ''}`.trim() || empId;
  };

  const getEmployeeCode = (empId) => getEmployee(empId)?.employment_details?.employee_code || '';

  const formatTime = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString('en-GB', { hour12: false });
    } catch { return iso; }
  };

  const punchTypeColor = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'in' || t === 'check_in') return 'bg-green-50 text-green-700 border-green-200';
    if (t === 'out' || t === 'check_out') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const sourceColor = (source) => {
    const map = {
      biometric: 'bg-green-50 text-green-700 border-green-200',
      mobile: 'bg-blue-50 text-blue-700 border-blue-200',
      web: 'bg-amber-50 text-amber-700 border-amber-200',
      manual: 'bg-amber-50 text-amber-700 border-amber-200',
      api: 'bg-purple-50 text-purple-700 border-purple-200',
    };
    return map[(source || '').toLowerCase()] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const inCount = punches.filter(p => ['in', 'check_in'].includes((p.punch_type || '').toLowerCase())).length;
  const outCount = punches.filter(p => ['out', 'check_out'].includes((p.punch_type || '').toLowerCase())).length;
  const missingCount = Math.max(0, inCount - outCount);

  const handleExport = () => {
    if (punches.length === 0) { toast.error('No punches to export'); return; }
    const rows = punches.map(p => ({
      Employee: getEmployeeName(p.employee_id),
      Employee_Code: getEmployeeCode(p.employee_id),
      Punch_Time: formatTime(p.punch_datetime || p.punch_time),
      Type: p.punch_type,
      Source: p.source,
      Device: p.device_id || '',
      Location: p.location_id || p.location_name || '',
    }));
    downloadCSV(rows, `punch_logs_${selectedDate}.csv`);
    toast.success('Exported');
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="punch-logs-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Clock className="inline-block w-8 h-8 mr-2 mb-1" />
              Punch Logs
            </h1>
            <p className="mt-1 text-sm text-slate-600">Live employee punch in/out records across biometric, mobile, and manual sources</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" data-testid="punch-export-btn" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
            <select data-testid="punch-company-select" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)}
              className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              data-testid="date-selector"
              className="h-10 px-4 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#0052FF] focus:border-[#0052FF]"
            />
          </div>
          <div className="flex gap-6 ml-auto">
            <div className="text-center">
              <p className="text-2xl font-semibold text-green-600 font-mono">{inCount}</p>
              <p className="text-xs text-slate-500">IN Punches</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-red-600 font-mono">{outCount}</p>
              <p className="text-xs text-slate-500">OUT Punches</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-amber-600 font-mono">{missingCount}</p>
              <p className="text-xs text-slate-500">Missing</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="punch-logs-table">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Employee</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Punch Time</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Device</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : punches.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-12 text-slate-500">
                  <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No punches recorded for {selectedDate}</p>
                </td></tr>
              ) : punches.map((p) => (
                <tr key={p.id} data-testid={`punch-log-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-4">
                    <p className="text-sm text-slate-900 font-medium">{getEmployeeName(p.employee_id)}</p>
                    <p className="text-xs text-slate-500 font-mono">{getEmployeeCode(p.employee_id)}</p>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-900 font-mono font-semibold">{formatTime(p.punch_datetime || p.punch_time)}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border uppercase ${punchTypeColor(p.punch_type)}`}>
                      {p.punch_type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${sourceColor(p.source)}`}>
                      {p.source || 'manual'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-700">{p.device_id || '-'}</td>
                  <td className="py-3 px-4 text-sm text-slate-700">{p.location_id || p.location_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">
            <strong>Multi-source Integration:</strong> Supports biometric devices, mobile app check-in, manual entry, and API imports. All punches are geo-tagged and device-tracked for audit.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default PunchLogs;
